import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { motion } from 'framer-motion'
import { Loader } from 'lucide-react'
import * as React from 'react'

import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { cn } from './utils'

const buttonVariants = cva(
  'w-fit inline-flex items-center justify-center whitespace-nowrap rounded-full text-xs text-foreground font-normal ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ',
  {
    variants: {
      radius: {
        full: 'rounded-full',
        lg: 'rounded-lg',
        md: 'rounded-md',
        sm: 'rounded-sm',
        xl: 'rounded-xl',
      },
      variant: {
        primary: 'button-primary button-shadow !text-white',
        primaryLight: 'button-primary-light !text-[var(--btn-primary-light-text)]',
        primaryOrange: 'button-primary-orange !text-white',
        secondary: 'button-secondary',
        destructive: 'button-destructive !text-white',
        warning: 'button-warning !text-white',
        rainbow: 'button-rainbow !text-white',
        ghost: 'button-ghost',
        black: 'button-black !text-white',
        green: 'button-green !text-white',
        glass: 'button-glass',
        glassBlue: 'button-glass-blue !text-white',
      },
      size: {
        default: 'h-9 px-3',
        sm: 'h-[32px] px-2',
        md: 'h-10 px-4 py-2',
        lg: 'h-11 px-8',
        icon: 'h-8 w-8',
        tiny: 'h-7 px-2 text-xs',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      radius: 'full',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  radius?: 'full' | 'md' | 'sm' | 'lg' | 'xl'
  pulsatingBorder?: boolean
  copyToClipboard?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      isLoading,
      variant,
      size,
      radius,
      asChild = false,
      pulsatingBorder = false,
      copyToClipboard,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const [copied, setCopied] = React.useState(false)
    const { toast } = useToast()
    const { t } = useLocalization()

    const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (copyToClipboard) {
        try {
          await navigator.clipboard.writeText(copyToClipboard)
          setCopied(true)

          // Show toast notification
          toast({
            title: t('Copied!', 'Kopierat!'),
            variant: 'success',
            duration: 2000,
          })

          // Reset to original content after 2 seconds
          setTimeout(() => {
            setCopied(false)
          }, 2000)
        } catch (err) {
          console.error('Failed to copy:', err)
          toast({
            title: t('Something went wrong', 'Något gick fel'),
            description: t(
              'Failed to copy to clipboard',
              'Kunde inte kopiera till urklipp'
            ),
            variant: 'destructive',
            duration: 3000,
          })
        }
      }

      // Call original onClick if provided
      onClick?.(e)
    }

    const Comp = asChild ? Slot : 'button'

    const buttonContent = (
      <>
        {isLoading ? (
          <Loader className="mr-2 h-4 w-4 animate-spin" />
        ) : copied ? (
          t('Copied!', 'Kopierat!')
        ) : (
          children
        )}
      </>
    )

    if (pulsatingBorder) {
      return (
        <motion.button
          className={cn(buttonVariants({ variant, size, radius, className }))}
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={handleCopy}
          animate={{
            boxShadow: [
              '0 0 0 0px rgba(71, 123, 255, 0)',
              '0 0 0 9px rgba(71, 123, 255, 1)',
              '0 0 0 0px rgba(71, 123, 255, 0)',
            ],
          }}
          transition={{
            duration: 2,
            repeat: 3,
            ease: 'easeInOut',
          }}
          {...(props as React.ComponentProps<typeof motion.button>)}
        >
          {buttonContent}
        </motion.button>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, radius, className }))}
        ref={ref}
        onClick={handleCopy}
        {...props}
      >
        {isLoading ? (
          <Loader className="h-4 w-4 animate-spin" />
        ) : copied ? (
          t('Copied!', 'Kopierat!')
        ) : (
          children
        )}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
